import Glyph from "@/components/Glyph";
import { useEffect, useState } from "react";
import { ArrowRight, Loader2, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const TRANSIT_SLUG = { Sun: "sun", Moon: "moon", Mercury: "mercury", Venus: "venus", Mars: "mars", Jupiter: "jupiter", Saturn: "saturn", Uranus: "uranus-neptune", Neptune: "uranus-neptune" };

export default function PlanetSheet({ planet, glyph, onClose, onSubscribe }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!planet) return;
    setInfo(null);
    setError(false);
    api.get(`/sky/planet/${planet.toLowerCase()}`).then(({ data }) => setInfo(data)).catch(() => setError(true));
  }, [planet]);

  return (
    <Sheet open={!!planet} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" data-testid="planet-sheet" className="max-w-md mx-auto bg-[#0B0F19] border-t border-[#D4AF37]/30 text-starlight rounded-t-3xl max-h-[85vh] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle data-testid="planet-sheet-title" className="text-2xl font-light text-starlight flex items-center gap-2">
            <Glyph ch={glyph} className="gold-text" /> {planet}{info && ` in ${info.sign}`}
          </SheetTitle>
          <SheetDescription className="text-[#A7B0C0]">
            {info ? `${info.degree}° ${info.sign}${info.retrograde ? " · Retrograde" : ""}` : "Reading the sky…"}
          </SheetDescription>
        </SheetHeader>
        {!info && !error && <Loader2 className="animate-spin gold-text my-8 mx-auto" size={24} />}
        {error && <p data-testid="planet-sheet-error" className="text-sm text-red-300 mt-4">Couldn't load this right now.</p>}
        {info && (
          <div className="mt-4 space-y-4 pb-4">
            <p data-testid="planet-sheet-meaning" className="text-base leading-relaxed text-[#D6D0C5]">{info.meaning}</p>
            {TRANSIT_SLUG[planet] && (
              <Link to={`/transits/${TRANSIT_SLUG[planet]}`} onClick={onClose} data-testid="planet-sheet-live"
                className="flex items-center justify-between bg-[#0F1523] border border-[#D4AF37]/30 rounded-2xl p-4 text-sm gold-text">
                Live {planet} position, next sign change and today's note <ArrowRight size={14} />
              </Link>
            )}
            {info.personal ? (
              <div data-testid="planet-sheet-personal" className="bg-[#0F1523] border border-[#D4AF37]/25 rounded-2xl p-4">
                <p className="text-[11px] uppercase tracking-[0.25em] gold-text mb-2">What it means for {info.sun_sign}</p>
                <p className="text-base leading-relaxed text-[#D6D0C5]">{info.personal}</p>
              </div>
            ) : (
              <button
                data-testid="planet-sheet-unlock"
                onClick={() => { onClose(); onSubscribe?.("what the planets mean for your sign"); }}
                className="w-full flex items-center justify-center gap-2 bg-[#0F1523] border border-[#D4AF37]/30 rounded-2xl p-4 text-sm gold-text"
              >
                <Lock size={14} /> See what it means for your sign with Premium
              </button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
